import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import type { FinishAdjustment } from 'utils/finishes';

/**
 * Session-scoped record of the backend's finish auto-corrections, keyed by
 * collection row id (Design Pass D-7, the persistent row marker).
 *
 * Lives in Redux rather than in the RTK Query cache because every have-list
 * write invalidates the HaveList tag, so a cache patch would be wiped by the
 * refetch it triggers. Rows do not persist the last adjustment server-side
 * (Known Limitation on the ticket), so the marker is lost on reload.
 */
interface FinishAdjustmentsState {
  byRowId: Record<string, FinishAdjustment>;
}

const initialState: FinishAdjustmentsState = { byRowId: {} };

const finishAdjustmentsSlice = createSlice({
  name: 'finishAdjustments',
  initialState,
  reducers: {
    recordFinishAdjustment: (
      state,
      action: PayloadAction<{ rowId: number; adjustment: FinishAdjustment | null | undefined }>
    ) => {
      const { rowId, adjustment } = action.payload;
      if (adjustment) state.byRowId[String(rowId)] = adjustment;
      else delete state.byRowId[String(rowId)];
    },
    clearFinishAdjustment: (state, action: PayloadAction<number>) => {
      delete state.byRowId[String(action.payload)];
    },
  },
});

export const { recordFinishAdjustment, clearFinishAdjustment } = finishAdjustmentsSlice.actions;
export const selectFinishAdjustments = (state: RootState) => state.finishAdjustments.byRowId;
export default finishAdjustmentsSlice.reducer;
